/** Block classic CSV injection payloads, not every cell starting with '='. */
export function isDangerousCsvCell(cell: string): boolean {
  const value = cell.trim();
  if (value.length < 2) return false;

  const upper = value.toUpperCase();
  const dangerousPrefixes = [
    '=CMD|',
    '+CMD|',
    '-CMD|',
    '@CMD|',
    '=HYPERLINK(',
    '=DDE(',
    '=POWERSHELL',
    '=MSHTA',
    '@SUM(',
  ];
  return dangerousPrefixes.some((prefix) => upper.startsWith(prefix));
}

/**
 * Validates CSV/TSV files for structural correctness and security issues.
 * Returns an error message string if validation fails, or null if all files pass.
 */
export async function validateCsvFiles(filesToCheck: File[]): Promise<string | null> {
  for (const file of filesToCheck) {
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.csv') && !lowerName.endsWith('.tsv')) continue;

    if (file.size === 0) {
      return `File '${file.name}' is empty.`;
    }

    const sep = lowerName.endsWith('.tsv') ? '\t' : ',';
    const text = await file.text();

    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentField = '';
    let inQuotes = false;
    let hasUnclosedQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (char === '"') {
        if (inQuotes && text[i + 1] === '"') {
          currentField += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === sep && !inQuotes) {
        currentRow.push(currentField);
        currentField = '';
      } else if ((char === '\r' || char === '\n') && !inQuotes) {
        if (char === '\r' && text[i + 1] === '\n') {
          i++;
        }
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
      } else {
        currentField += char;
      }
    }

    if (currentRow.length > 0 || currentField !== '') {
      currentRow.push(currentField);
      rows.push(currentRow);
    }

    if (inQuotes) {
      hasUnclosedQuotes = true;
    }

    if (rows.length === 0 || (rows.length === 1 && rows[0].length === 1 && rows[0][0].trim() === '')) {
      return `File '${file.name}' is empty.`;
    }

    const headers = rows[0];
    if (headers.some((col) => !col.trim())) {
      return `File '${file.name}' has empty or missing column headers. Please correct them.`;
    }

    const seenHeaders = new Set<string>();
    for (const header of headers) {
      const normalized = header.trim().toLowerCase();
      if (seenHeaders.has(normalized)) {
        return `File '${file.name}' has duplicate column headers: "${header}". Please ensure all header names are unique.`;
      }
      seenHeaders.add(normalized);
    }

    if (hasUnclosedQuotes) {
      return `File '${file.name}' has malformed syntax: Unclosed double quotes detected.`;
    }

    const expectedCount = headers.length;
    for (let rIdx = 1; rIdx < rows.length; rIdx++) {
      const row = rows[rIdx];
      if (rIdx === rows.length - 1 && row.length === 1 && row[0].trim() === '') {
        continue;
      }

      if (row.length !== expectedCount) {
        return `File '${file.name}' has a column count mismatch on row ${rIdx + 1}. Expected ${expectedCount} columns (defined by headers), but found ${row.length} columns. This is often caused by unquoted commas inside text fields (e.g. "John,Smith" instead of "John,Smith").`;
      }

      for (let cIdx = 0; cIdx < row.length; cIdx++) {
        const cell = row[cIdx]?.trim() || '';
        if (isDangerousCsvCell(cell)) {
          return `File '${file.name}' contains a potentially unsafe spreadsheet formula on row ${rIdx + 1}, column '${headers[cIdx]}': "${cell}". For security, known formula-injection patterns are not allowed in data uploads.`;
        }
      }
    }
  }
  return null;
}
